import { useEffect, useState } from 'react';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { useDebounce } from '@gonfalon/async/useDebounce';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import {
  ListEmptyState,
  ListEmptyStateDescription,
  ListEmptyStateHeading,
} from '@gonfalon/launchpad-experimental/ListEmptyState';
import { NewTabLink } from '@gonfalon/launchpad-experimental/NewTabLink';
import type { schemas } from '@gonfalon/openapi/types';
import { getMetricEventDefinitionInstancesByDataSourceQuery } from '@gonfalon/rest-api/queries/metricEvents';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Alert, Button, Disclosure, DisclosurePanel, Link, ProgressBar } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { EventDataPreviewTable } from './EventDataPreviewTable';

import styles from './EventDataPreview.module.css';

// Keep in sync with ALL_WAREHOUSE_INTEGRATION_KEYS in @gonfalon/experiments/isExperimentMetricDataSourceMismatched.
// packages/metrics cannot depend on @gonfalon/experiments due to a workspace cycle (metrics → experiments → empty-state → measured-rollouts → metrics).
const ALL_WAREHOUSE_INTEGRATION_KEYS = [
  'snowflake-experimentation',
  'databricks-experimentation',
  'redshift-experimentation',
  'bigquery-experimentation',
];

const VIEW_INCOMING_EVENTS_DOC_LINK = getDocumentationUrl('home/metrics/view-incoming-events');
const VIEW_EVENTS_FROM_SDKS_DOC_LINK = getDocumentationUrl(
  '/home/observability/view-incoming-events#view-events-from-your-sdks',
);

export type EventDataPreviewProps = {
  eventKey: string | undefined;
  eventKind: string;
  dataSourceKey?: string;
  // whether to expand the disclosure by default
  defaultExpanded?: boolean;
  // whether the metric is numeric - required for the table to display the value column
  isNumeric?: boolean;
  // whether to display the empty state when there is no data
  displayEmptyState?: boolean;
  // maximum number of events to display (default: 10, which is the API max)
  eventLimit?: number;
  // whether to show the "Preview event data" label (default: true)
  showPreviewLabel?: boolean;
  // whether to show the "Showing events received in the last 90 days" text (default: false)
  showEventTimeInfo?: boolean;
  // event data containing sources for Snowflake detection
  event?: schemas['EventActivityResponseItemRep'];
};

export function EventDataPreview({
  eventKey,
  eventKind,
  dataSourceKey,
  defaultExpanded = false,
  isNumeric = false,
  displayEmptyState = true,
  eventLimit = 10,
  showPreviewLabel = true,
  showEventTimeInfo = false,
  event,
}: EventDataPreviewProps) {
  const projectKey = useProjectKey();
  const { selectedEnvironment } = useProjectContext();
  const environmentKey = selectedEnvironment.key;

  const warehouseSources = event?.sources?.filter((s) => ALL_WAREHOUSE_INTEGRATION_KEYS.includes(s)) ?? [];
  const isWarehouseOnly = warehouseSources.length > 0 && warehouseSources.length === (event?.sources?.length ?? 0);
  const showWarehouseNotification = warehouseSources.length > 0;

  // Debounce the event key to prevent API calls on every keystroke
  const [debouncedEventKey, setDebouncedEventKey] = useState('');
  const setDebouncedEventKeyHandler = useDebounce(setDebouncedEventKey, 300);

  useEffect(() => {
    if (!eventKey) {
      return;
    }
    setDebouncedEventKeyHandler(eventKey);
  }, [eventKey, setDebouncedEventKeyHandler]);

  const shouldFetch = debouncedEventKey.length >= 3;

  const metricEventInstancesByDataSourceQuery = useQuery({
    ...getMetricEventDefinitionInstancesByDataSourceQuery({
      projectKey,
      environmentKey,
      eventKey: debouncedEventKey,
      eventKind,
      dataSourceKey: dataSourceKey as string,
    }),
    enabled: shouldFetch,
  });

  const { data: metricEventInstances, isLoading, isFetching, isError } = metricEventInstancesByDataSourceQuery;

  const events = metricEventInstances?.sampleEvents?.slice(0, eventLimit) || [];

  // Check if we're waiting for debounce to complete (eventKey exists and is long enough, but debouncedEventKey hasn't caught up)
  const isWaitingForDebounce = Boolean(eventKey && eventKey.length >= 3 && eventKey !== debouncedEventKey);

  // Consider loading if: query is loading/fetching, waiting for debounce, or query enabled but no data yet
  const isDataLoading = isLoading || isFetching || isWaitingForDebounce || (shouldFetch && !metricEventInstances);

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Auto-expand when there's no data to show the "no data" message
  useEffect(() => {
    if (!isDataLoading && events.length === 0) {
      setIsExpanded(true);
    }
  }, [isDataLoading, events.length]);

  // Reset the expanded state when the event key changes
  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [eventKey, defaultExpanded]);

  const renderNoDataFound = () => {
    if (isWarehouseOnly) {
      return (
        <ListEmptyState>
          <ListEmptyStateHeading>No data available</ListEmptyStateHeading>
          <ListEmptyStateDescription>
            Event data from data warehouse integrations does not display here.{' '}
            <Link href={VIEW_EVENTS_FROM_SDKS_DOC_LINK} target="_blank">
              Learn more
            </Link>
            .
          </ListEmptyStateDescription>
        </ListEmptyState>
      );
    }

    // Generic empty state for other cases
    return (
      <div className={styles.noDataFound}>
        <span>No event data available to preview.</span>
        <NewTabLink href={VIEW_INCOMING_EVENTS_DOC_LINK} text="Learn more" />
      </div>
    );
  };

  if (!displayEmptyState && (isDataLoading || !events.length)) {
    return null;
  }

  return (
    <div className={styles.eventDataPreviewWrapper}>
      <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
        {({ isExpanded: disclosureExpanded }) => (
          <>
            {showPreviewLabel && (
              <Button slot="trigger" variant="minimal" className={styles.eventPreviewButton} isDisabled={isDataLoading}>
                <span className={styles.disclosureIcon}>
                  <Icon
                    name={disclosureExpanded ? 'chevron-down' : 'chevron-right'}
                    size="small"
                    aria-label={`${disclosureExpanded ? 'Collapse' : 'Expand'} event preview table`}
                  />
                </span>
                <span>Preview event data </span>
                {isDataLoading && <ProgressBar size="small" isIndeterminate aria-label="Loading recent events" />}
              </Button>
            )}
            {showEventTimeInfo && !isDataLoading && events.length > 0 && (
              <span className={styles.eventTimeInfo}>
                <span className={styles.eventTimeInfoText}>Showing events received in the last 90 days</span>
              </span>
            )}
            {showEventTimeInfo && isDataLoading && (
              <span className={styles.eventTimeInfo}>
                <ProgressBar size="small" isIndeterminate aria-label="Loading recent events" />
              </span>
            )}
            <DisclosurePanel className={styles.disclosurePanel}>
              {showWarehouseNotification && !isWarehouseOnly && (
                <Alert className={styles.eventNotification}>
                  Event data only available from sources sent directly to LaunchDarkly and excludes events from data
                  warehouse.{' '}
                  <Link href={VIEW_EVENTS_FROM_SDKS_DOC_LINK} target="_blank" className="link">
                    Learn more
                  </Link>
                </Alert>
              )}
              {!isDataLoading && (isError || !events.length) && renderNoDataFound()}
              {!isDataLoading && !isError && events.length > 0 && (
                <EventDataPreviewTable metricEventInstances={events} isNumeric={isNumeric} eventKind={eventKind} />
              )}
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  );
}
